import * as React from 'react';
import { useNavigate } from 'react-router';
import useNotifications from '../hooks/useNotifications/useNotifications';
import { uploadJob } from '../../services/jobService';  // ✅ your API
import JobForm from './JobForm';  // ✅ you’ll create a form like EmployeeForm
import PageContainer from './PageContainer';

const INITIAL_FORM_VALUES = {
  jobTitle: '',
  department: '',
  location: '',
  employmentType: 'Full-time',
  jobSummary: '',
  responsibilities: [],
  requiredSkills: [],
  preferredSkills: [],
  experienceLevel: '',
  educationRequirements: '',
  minSalary: '',
  maxSalary: '',
  currency: 'INR',
};

export default function JobCreate() {
  const navigate = useNavigate();
  const notifications = useNotifications();

  const [formState, setFormState] = React.useState(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback((newFormValues) => {
    setFormState((prev) => ({
      ...prev,
      values: newFormValues,
    }));
  }, []);

  const setFormErrors = React.useCallback((newFormErrors) => {
    setFormState((prev) => ({
      ...prev,
      errors: newFormErrors,
    }));
  }, []);

  const handleFormFieldChange = React.useCallback(
    (name, value) => {
      const newFormValues = { ...formValues, [name]: value };

      // simple validation
      const newErrors = { ...formErrors };
      if (!newFormValues[name] && ['jobTitle', 'department', 'location', 'jobSummary'].includes(name)) {
        newErrors[name] = `${name} is required`;
      } else {
        delete newErrors[name];
      }

      setFormValues(newFormValues);
      setFormErrors(newErrors);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
    setFormErrors({});
  }, [setFormValues, setFormErrors]);

  const handleFormSubmit = React.useCallback(async () => {
    // validate required fields
    const requiredFields = ['jobTitle', 'department', 'location', 'jobSummary'];
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formValues[field]) {
        newErrors[field] = `${field} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      await uploadJob(formValues); // ✅ POST /api/job/upload

      notifications.show('Job created successfully.', {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate('/app/jobs');
    } catch (createError) {
      notifications.show(
        `Failed to create job. Reason: ${createError.message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [formValues, navigate, notifications, setFormErrors]);

  return (
    <PageContainer
      title="New Job"
      breadcrumbs={[{ title: 'Jobs', path: '/jobs' }, { title: 'New' }]}
    >
      <JobForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
